import { classnames } from '@/helpers';
import { AnchorHTMLAttributes, HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { BUTTON_PADDING } from '../Button/Button';
import { ButtonSize, ButtonVariant } from '../types';
import css from './Fab.module.scss';
type Icon = JSX.Element;

type FabButtonVariant = ButtonVariant | 'borderless';

type GenericFabProps = {
  variant?: FabButtonVariant;
  size: ButtonSize;
  floating?: boolean;
  icon?: Icon;
  rightIcon?: Icon;
  label?: string;
  disabled?: boolean;
  to?: string;
};

export type FabProps = GenericFabProps &
  ((HTMLAttributes<HTMLButtonElement> & { to?: never }) | (AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }));

const getVariant = (variant: FabButtonVariant, size: ButtonSize, floating: boolean, disabled: boolean) => {
  const variants: {
    [key in FabButtonVariant]?: string;
  } = {
    primary: classnames({
      [`text-${size}`]: true,
      'shadow-lg': !disabled && floating,
      'bg-bg_secondary_bold text-fg_disabled border border-border_bold opacity-50 cursor-not-allowed pointer-events-none':
        disabled,
      'hover:bg-bg_primary_hover active:bg-bg_primary_hover text-fg_on_primary bg-bg_primary': !disabled,
    }),
    secondary: classnames({
      'bg-bg_secondary_bold': true,
      [`text-${size}`]: true,
      'text-fg_primary': true,
      'hover:bg-bg_secondary_hover': !disabled,
      'pointer-events-none': disabled,
      'active:bg-bg_secondary_bold': disabled,
      'shadow-lg': !disabled && floating,
      'opacity-40': disabled,
    }),
    tertiary: classnames({
      'bg-brand': true,
      [`text-${size}`]: true,
      'text-white': true,
      'opacity-20': disabled,
      'hover:bg-brand-200': !disabled,
      'shadow-lg': !disabled && floating,
    }),
    link: classnames({
      [`text-${size}`]: true,
      'opacity-20': disabled,
    }),
    borderless: classnames({
      [`text-${size}`]: true,
      'opacity-20': disabled,
      'bg-transparent': true,
    }),
  };

  return variants[variant];
};

const base = `flex items-center justify-center rounded-full transition-transform active:scale-95`;
const btnClasses = (
  variant: FabButtonVariant,
  size: ButtonSize,
  floating: boolean,
  disabled?: boolean,
  className?: string,
) => twMerge(classnames(base, getVariant(variant, size, floating, disabled)), className);

const Fab = ({
  variant = 'primary',
  label,
  size,
  floating,
  icon,
  disabled,
  children,
  className = '',
  rightIcon,
  to,
  ...props
}: FabProps) => {
  const commonClassNames = `${css.button} ${css[size]} ${css[variant]} ${disabled ? css.disabled : ''} ${
    label || children ? `!h-auto !w-auto ${BUTTON_PADDING[size]} gap-x-md` : ''
  } ${btnClasses(variant, size, floating, disabled, className)}`;

  const content = (
    <>
      {icon && <span className={`${css.icon} ${css[size]}`}>{icon}</span>}
      {label ?? children}
      {rightIcon && <span className={`${css.icon} ${css[size]}`}>{rightIcon}</span>}
    </>
  );

  if (to) {
    return (
      <a href={to} className={commonClassNames} {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {content}
      </a>
    );
  }
  return (
    <button className={commonClassNames} {...(props as HTMLAttributes<HTMLButtonElement>)}>
      {content}
    </button>
  );
};

export default Fab;
